import React from 'react';
import PropTypes from 'prop-types';
import { LuBoxes, LuTriangleAlert, LuWrench } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import useStockText from '../stockText';

// Category visuals: sarf = teal, demirbaş = violet
export const CATEGORY_GRADIENTS = {
  sarf: 'tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30',
  demirbas: 'tw-from-violet-400 tw-to-indigo-600 tw-shadow-violet-500/30',
  unknown: 'tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30',
};

const CATEGORY_TONES = {
  sarf: 'tw-bg-teal-500/10 tw-text-teal-700 tw-ring-teal-500/25 dark:tw-text-teal-300',
  demirbas: 'tw-bg-violet-500/10 tw-text-violet-700 tw-ring-violet-500/25 dark:tw-text-violet-300',
  unknown: 'tw-bg-slate-500/10 tw-text-slate-600 tw-ring-slate-500/20 dark:tw-text-slate-300',
};

export const categoryKey = (value) => (value === 'sarf' || value === 'demirbas' ? value : 'unknown');

/** Sarf / Demirbaş pill. */
export const CategoryBadge = ({ value }) => {
  const { categoryLabel } = useStockText();
  const key = categoryKey(value);
  const Icon = key === 'demirbas' ? LuWrench : LuBoxes;
  return (
    <span className={`tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-whitespace-nowrap tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-ring-1 tw-ring-inset ${CATEGORY_TONES[key]}`}>
      {key !== 'unknown' && <Icon aria-hidden="true" className="tw-h-3 tw-w-3" />}
      {categoryLabel(value)}
    </span>
  );
};

CategoryBadge.propTypes = { value: PropTypes.string };

/** Aktif / İnaktif pill (other stored text is shown as it is). */
export const StockStatusBadge = ({ value }) => {
  const { statusLabel } = useStockText();
  if (!value) return <span className="tw-text-slate-400 dark:tw-text-slate-500">-</span>;
  const active = value === 'aktif';
  return (
    <Badge tone={active ? 'success' : 'neutral'}>
      <span aria-hidden="true" className={`tw-h-1.5 tw-w-1.5 tw-rounded-full ${active ? 'tw-bg-emerald-500' : 'tw-bg-slate-400'}`} />
      {statusLabel(value)}
    </Badge>
  );
};

StockStatusBadge.propTypes = { value: PropTypes.string };

/** Customer / project / ownership as a quiet chip; "—" when empty. */
export const ScopeChip = ({ value }) => (
  value
    ? <span className="tw-inline-block tw-max-w-full tw-truncate tw-rounded-lg tw-bg-slate-900/[0.05] tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-slate-600 dark:tw-bg-white/[0.07] dark:tw-text-slate-300">{value}</span>
    : <span className="tw-text-slate-400 dark:tw-text-slate-500">—</span>
);

ScopeChip.propTypes = { value: PropTypes.string };

/** Remaining quantity; red with a warning icon at or below the minimum. */
export const RemainingValue = ({ value, low }) => {
  const { label } = useStockText();
  if (!low) return <span className="tw-tabular-nums">{value === undefined || value === null || value === '' ? '-' : String(value)}</span>;
  return (
    <span title={label('table.lowHint')} className="tw-inline-flex tw-items-center tw-gap-1 tw-font-bold tw-tabular-nums tw-text-rose-600 dark:tw-text-rose-300">
      <LuTriangleAlert aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
      {value === undefined || value === null || value === '' ? '0' : String(value)}
    </span>
  );
};

RemainingValue.propTypes = { value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), low: PropTypes.bool };

/** Two letters for a stock or person name. */
export const nameInitials = (name) => {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  return `${words[0][0] || ''}${words.length > 1 ? words[words.length - 1][0] || '' : ''}`.toLocaleUpperCase('tr-TR');
};
